import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { TasksGroups } from "./tasks/TasksGroups";
import { EmptyState, PageHeader, PageMain } from "components/molecules";
import { useAdminMe, useAllIssues, useArchivedIssues } from "api/hooks";
import { IssueApi, issuesApi } from "api/issuesApi";
import { TaskCard } from "./tasks/TaskCard";
import { PreviewTaskModal } from "components/templates/CreateTaskModal/PreviewTaskModal";
import { TASKS_STATUSES } from "consts/tasks";
import { PaziaLoader } from "components/atoms";

const tabs = [
  {
    key: "all" as const,
    label: "Wszystkie",
  },
  {
    key: "mine" as const,
    label: "Moje zadania",
  },
  {
    key: "created-by-me" as const,
    label: "Stworzone przeze mnie",
  },
  {
    key: "archive" as const,
    label: "Archiwum",
  },
];
export const TasksPage = () => {
  const { me } = useAdminMe();
  const { isLoading, issues } = useAllIssues();
  const { isLoading: loadingArchived, issues: archivedIssues } = useArchivedIssues();
  const [selectedIssue, setSelectedIssue] = useState<IssueApi | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "mine" | "archive" | "created-by-me">("mine");

  return (
    <PageMain>
      <PageHeader>Zadania</PageHeader>

      <div className="mb-4 border-b border-slate-200 dark:border-slate-700">
        <ul className="text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar">
          {tabs.map((tab) => (
            <li
              key={tab.key}
              className={`pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8 ${
                activeTab === tab.key
                  ? "text-primary-500"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="whitespace-nowrap cursor-pointer">{tab.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {issues && activeTab === "all" && (
        <>
          {isLoading ? (
            <PaziaLoader position="center" />
          ) : (
            <DraggableTaskTable issues={issues} setSelectedIssue={setSelectedIssue} />
          )}
        </>
      )}
      {issues && activeTab === "mine" && (
        <>
          {isLoading ? (
            <PaziaLoader position="center" />
          ) : (
            <DraggableTaskTable
              issues={issues.filter((issue) => issue.assignees?.some((a) => a.id === me.userId))}
              setSelectedIssue={setSelectedIssue}
            />
          )}
        </>
      )}
      {archivedIssues && activeTab === "archive" && (
        <div>
          {loadingArchived ? (
            <PaziaLoader position="center" />
          ) : (
            archivedIssues.map((issue) => <TaskCard issue={issue} onClick={() => setSelectedIssue(issue)} />)
          )}
        </div>
      )}
      {issues && activeTab === "created-by-me" && (
        <>
          {isLoading ? (
            <PaziaLoader position="center" />
          ) : (
            <DraggableTaskTable
              issues={issues.filter((issue) => issue.createdBy?.id === me.userId)}
              setSelectedIssue={setSelectedIssue}
            />
          )}
        </>
      )}

      <PreviewTaskModal issueId={selectedIssue?.id} onCloseRequest={() => setSelectedIssue(null)} />
    </PageMain>
  );
};

const DraggableTaskTable = ({
  issues,
  setSelectedIssue,
}: {
  issues: IssueApi[];
  setSelectedIssue: (i: IssueApi) => void;
}) => {
  const [issuesGroupedByStatus, setIssuesGroupedByStatus] = useState(mapIssues(issues));

  useEffect(() => {
    setIssuesGroupedByStatus(mapIssues(issues));
  }, [issues]);

  // @ts-ignore
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    const sourceStatus = parseInt(source.droppableId);
    const destinationStatus = parseInt(destination.droppableId);

    if (sourceStatus === destinationStatus) {
      return;
    }

    const sourceIssues = Array.from(issuesGroupedByStatus[sourceStatus]);
    const [movedIssue] = sourceIssues.splice(source.index, 1);
    movedIssue.status = destinationStatus;

    const destinationIssues = Array.from(issuesGroupedByStatus[destinationStatus]);
    destinationIssues.splice(destination.index, 0, movedIssue);

    setIssuesGroupedByStatus({
      ...issuesGroupedByStatus,
      [sourceStatus]: sourceIssues,
      [destinationStatus]: destinationIssues,
    });

    issuesApi.changeStatus(movedIssue.id, destinationStatus);
    // Optionally, update the issue status in your backend here.
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-12 gap-x-4 gap-y-8">
        {Object.keys(issuesGroupedByStatus).map((status) => (
          <Droppable droppableId={status} key={status}>
            {(provided) => (
              <TasksGroups title={getTitle(status)} ref={provided.innerRef} {...provided.droppableProps}>
                {issuesGroupedByStatus[status].map((issue, index) => (
                  <Draggable key={issue.id} draggableId={issue.id.toString()} index={index}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <TaskCard issue={issue} onClick={() => setSelectedIssue(issue)} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TasksGroups>
            )}
          </Droppable>
        ))}
      </div>
      {issues.length === 0 && <EmptyState title="Brak zadań" description="Nic do wyświetlenia" />}
    </DragDropContext>
  );
};

const mapIssues = (issues: IssueApi[]): Record<string, IssueApi[]> => {
  return issues.reduce(
    (acc, issue) => {
      const status = issue.status;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(issue);
      return acc;
    },
    {
      [TASKS_STATUSES.TODO]: [],
      [TASKS_STATUSES.INPROGRESS]: [],
      [TASKS_STATUSES.DONE]: [],
      4: [],
    } as Record<string, IssueApi[]>
  );
};

const getTitle = (status: string) => {
  switch (parseInt(status)) {
    case 1:
      return "Do zrobienia 🖋️";
    case 2:
      return "W trakcie ✌️";
    case 3:
      return "Zakończone 🎉";
    default:
      return "";
  }
};
